import { reactive } from 'vue'
import Color from 'colorjs.io'

export const store = reactive({
    // define store properties and defaults
    baseColor: null,
    numberOfHues: 6,
    numberOfStops: 5,
    splitType: 'even',
    multiplierType: 'none',
    showLabels: false,
    currentColumnColors: [],

    harmonicSplits: {
        6:  [0, 30, 150, 180, 210, 330],
        8:  [0, 30, 60, 150, 180, 210, 270, 330],
        10: [0, 36, 72, 144, 180, 216, 252, 288, 324, 108],
        12: [0, 25, 45, 70, 120, 150, 180, 200, 225, 270, 300, 335],
        14: [0, 10, 40, 60, 70, 80, 140, 150, 160, 190, 250, 280, 310, 350]
    },

    // Tailwind-like steps for perceptual scaling
    lightnessSplits: {
        5: [50, 200, 500, 700, 900],
        7: [50, 100, 300, 500, 700, 800, 950],
        11: [50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950]
    },

    standardMultipliers: {
        0: 0.9,    // Red
        15: 0.95,  // Red-Orange
        30: 1.3,   // Orange
        45: 1.4,   // Yellow-Orange
        60: 1.5,   // Yellow
        75: 1.4,   // Yellow-Green
        90: 1.3,   // Lime
        105: 1.2,  // Yellow-Green
        120: 1.1,  // Green
        135: 1.0,  // Green-Teal
        150: 0.9,  // Teal
        165: 0.8,  // Cyan-Teal
        180: 0.75, // Cyan
        195: 0.8,  // Sky-Cyan
        210: 0.8,  // Sky
        225: 0.7,  // Sky-Blue
        240: 0.65, // Blue
        255: 0.6,  // Blue-Violet
        270: 0.55, // Violet
        285: 0.6,  // Purple-Violet
        300: 0.7,  // Purple
        315: 0.75, // Pink-Purple
        330: 0.8,  // Pink
        345: 0.85  // Red-Pink
    },

    maxChromas: {
        0:   0.22,    // Red
        15:  0.24,
        30:  0.28,   // Orange
        45:  0.31,
        60:  0.32,   // Yellow (highest)
        75:  0.30,
        90:  0.28, // Lime
        105: 0.25,
        120: 0.24,  // Green
        135: 0.21,
        150: 0.20, // Teal
        165: 0.18,
        180: 0.16,  // Cyan
        195: 0.15,
        210: 0.16, // sky Blue
        225: 0.17,
        240: 0.14,  // Blue (our base color range)
        255: 0.13,
        270: 0.12,  // Violet (lowest)
        285: 0.13,
        300: 0.15,  // Purple
        315: 0.18,
        330: 0.19, // Pink
        345: 0.21,
    },

    // define methods
    setNumberOfHues(value) {
        this.numberOfHues = value
    },
    setNumberOfStops(value) {
        this.numberOfStops = value
    },
    setSplitType(value) {
        this.splitType = value
    },
    setMultiplierType(value) {
        this.multiplierType = value
    },
    setBaseColor(value) {
        this.baseColor = value
    },
    setShowLabels(value) {
        this.showLabels = value
    },

    // Get associated multiplier for each degree
    interpolateMultiplier(hue, multiplierObject) {

        // 1. Ensure degree is within 360
        let normalizedHue = ((hue % 360) + 360) % 360
        
        // 2a. If exact match is found, return immediately
        if (multiplierObject[normalizedHue] !== undefined) {
            return multiplierObject[normalizedHue]
        }

        // 2b. Else, use surrounding keys (upper and lower limit) to get a multpliert
        const keys = Object.keys(multiplierObject).map(Number).sort((a, b) => a - b)

        let lowerKey = keys[0]
        let upperKey = keys[keys.length - 1]
        
        for (let i = 0; i < keys.length - 1; i++) {
            if (normalizedHue >= keys[i] && normalizedHue <= keys[i + 1]) {
                lowerKey = keys[i]
                upperKey = keys[i + 1]
                break
            }
        }
        
        // Handle wrap-around (e.g., between 345° and 0°)
        if (normalizedHue > keys[keys.length - 1] || normalizedHue < keys[0]) {
            lowerKey = keys[keys.length - 1]
            upperKey = keys[0] + 360
            if (normalizedHue < keys[0]) {
                normalizedHue += 360
            }
        }
        /**
         * Linear interplation to get mutliplier or of 'in-between' hue degrees
         * Calculates how far (progress) is the hue between these two value. This is used to calculate the return 
         * The return is the value between lowerValue and upperValue depending on how close normalizedHue is to each
         */
        const lowerValue = multiplierObject[lowerKey % 360]
        const upperValue = multiplierObject[upperKey % 360]
        const progress = (normalizedHue - lowerKey) / (upperKey - lowerKey)
        
        return lowerValue + (upperValue - lowerValue) * progress
    },
        
    // For 'standard' multiplier type  
    getChromaForStandard(hue) {
        // Skip baseColor's hue column - return original chroma
        if (this.baseColor && Math.abs(hue - this.baseColor.h) < 1) {
            return this.baseColor.c;
        }

        const baseChroma = this.baseColor.c;

        // 1. Interpolate Base to get estimated multiplier of baseColor 
        const estBaseMultiplier = this.interpolateMultiplier(this.baseColor.h, this.standardMultipliers);
        // 2. Interpolate Current hue to get estimated multiplier of this hue
        const estCurrentHueMultiplier = this.interpolateMultiplier(hue, this.standardMultipliers);
        // 3. Calculate the ratio
        const unifiedMultiplier = estCurrentHueMultiplier / estBaseMultiplier;
        // 4. Apply ratio to base chroma to get modified chroma for this hue
        const perceivedChroma = unifiedMultiplier * baseChroma

        return perceivedChroma;
    },

    // For 'relative' multiplier type
    getChromaForRelative(hue) {
        if (!this.baseColor) return this.baseColor?.c || 0.1;
        
        const baseHue = this.baseColor.h;
        const baseMaxChroma = this.interpolateMultiplier(baseHue, this.maxChromas);
        const baseUtilization = this.baseColor.c / baseMaxChroma;

        const targetMaxChroma = this.interpolateMultiplier(hue, this.maxChromas);
        const multiplier = (targetMaxChroma * baseUtilization) / this.baseColor.c;
        
        return this.baseColor.c * multiplier;
    },

    getChromaForHue(hue) {
        if (this.multiplierType === 'none') {
            return this.baseColor.c;
        }
        
        if (this.multiplierType === 'standard') {
            return this.getChromaForStandard(hue);
        }
        
        if (this.multiplierType === 'relative') {
            return this.getChromaForRelative(hue);
        }
        
        return this.baseColor?.c || 0.1;
    },
    
    // Hue-specific profiles for perceptual uniformity
    getHueProfile(hue) {
        const h = (hue + 360) % 360;
        
        if ((h >= 338.2 && h < 360) || (h >= 0 && h < 5.4)) {
            return { name: 'pink',     lRange: [0.97, 0.23], cRange: [0.04, 0.15], hueShift: 6 };
        }
        if (h >= 5.4   && h < 20.9) {
            return { name: 'rose',     lRange: [0.97, 0.23], cRange: [0.04, 0.15], hueShift: 5 };
        }
        if (h >= 20.9  && h < 36.5) {
            return { name: 'red',      lRange: [0.97, 0.23], cRange: [0.05, 0.16], hueShift: 8 };
        }
        if (h >= 36.5  && h < 58.8) {
            return { name: 'orange',   lRange: [0.97, 0.23], cRange: [0.03, 0.12], hueShift: 12 };
        }
        if (h >= 58.8  && h < 78.1) {
            return { name: 'amber',    lRange: [0.97, 0.23], cRange: [0.04, 0.12], hueShift: 15 };
        }
        if (h >= 78.1  && h < 108.5) {
            return { name: 'yellow',   lRange: [0.97, 0.23], cRange: [0.03, 0.10], hueShift: 18 };
        }
        if (h >= 108.5 && h < 140.2) {
            return { name: 'lime',     lRange: [0.97, 0.23], cRange: [0.05, 0.13], hueShift: 6 };
        }
        if (h >= 140.2 && h < 156.0) {
            return { name: 'green',    lRange: [0.97, 0.23], cRange: [0.06, 0.14], hueShift: 4 };
        }
        if (h >= 156.0 && h < 172.5) {
            return { name: 'emerald',  lRange: [0.97, 0.23], cRange: [0.07, 0.16], hueShift: 3 };
        }
        if (h >= 172.5 && h < 198.9) {
            return { name: 'teal',     lRange: [0.97, 0.23], cRange: [0.06, 0.18], hueShift: 5 };
        }
        if (h >= 198.9 && h < 226.3) {
            return { name: 'cyan',     lRange: [0.97, 0.23], cRange: [0.08, 0.20], hueShift: 8 };
        }
        if (h >= 226.3 && h < 248.6) {
            return { name: 'sky',      lRange: [0.97, 0.23], cRange: [0.07, 0.19], hueShift: 6 };
        }
        if (h >= 248.6 && h < 268.5) {
            return { name: 'blue',     lRange: [0.97, 0.23], cRange: [0.07, 0.18], hueShift: 5 };
        }
        if (h >= 268.5 && h < 284.9) {
            return { name: 'indigo',   lRange: [0.97, 0.23], cRange: [0.06, 0.17], hueShift: 6 };
        }
        if (h >= 284.9 && h < 298.3) {
            return { name: 'violet',   lRange: [0.97, 0.23], cRange: [0.05, 0.17], hueShift: 7 };
        }
        if (h >= 298.3 && h < 313.0) {
            return { name: 'purple',   lRange: [0.97, 0.23], cRange: [0.05, 0.16], hueShift: 8 };
        }
        if (h >= 313.0 && h < 338.2) {
            return { name: 'fuchsia',  lRange: [0.97, 0.23], cRange: [0.05, 0.16], hueShift: 9 };
        }

        return { name: 'pink', lRange: [0.97, 0.23], cRange: [0.04, 0.15], hueShift: 6 };
    },
 
    // Generate hue degrees based on splitType and numberOfHues
    getHueDegrees() {
        if (!this.baseColor) return []
        
        const baseHue = this.baseColor.h || 0
        
        if (this.splitType == 'even') {
            const step = 360 / this.numberOfHues
            return Array.from({ length: this.numberOfHues }, (_, i) => {
                let hue = baseHue + (i * step)
                while (hue >= 360) hue -= 360
                while (hue < 0) hue += 360
                return Math.round(hue * 1000) / 1000
            })
        } else if (this.splitType == 'harmonic') {
            const offsets = this.harmonicSplits[this.numberOfHues] || []
            return offsets.map(offset => {
                let hue = baseHue + offset
                while (hue >= 360) hue -= 360
                while (hue < 0) hue += 360
                return Math.round(hue * 1000) / 1000
            })
        }
        
        return []
    },
  
    // Generate complete color data for each hue
    getHueColors() {
        const hueDegrees = this.getHueDegrees()
        if (!this.baseColor || hueDegrees.length === 0) return []
        
        return hueDegrees.map((hue, index) => {
            try {
                const lightness = typeof this.baseColor.l === 'number' ? this.baseColor.l : 0.5
                const baseChroma = typeof this.baseColor.c === 'number' ? this.baseColor.c : 0.1
                const hueValue = typeof hue === 'number' ? hue : 0
                const alpha = typeof this.baseColor.alpha === 'number' ? this.baseColor.alpha : 1
                
                const adjustedChroma = this.getChromaForHue(hueValue)
                
                // Calculate effective multiplier for display purposes
                const multiplier = adjustedChroma / baseChroma
                
                const oklchString = `oklch(${lightness} ${adjustedChroma} ${hueValue})`
                const oklchColor = new Color(oklchString)
                
                return {
                    index,
                    hue: hueValue,
                    multiplier: Math.round(multiplier * 100) / 100,
                    oklch: {
                        l: lightness,
                        c: adjustedChroma,
                        h: hueValue,
                        alpha: alpha
                    },
                    hex: oklchColor.to('srgb').toString({ format: 'hex' }),
                    cssColor: oklchString
                }
            } catch (error) {
                console.warn('Error creating color for hue:', hue, error)
                return {}
            }
        })
    },

    // ADVANCED: Generate perceptually uniform lightness palette for a specific hue
    generateAdvancedLightnessPalette(hueValue, baseL, baseC) {
        const steps = this.lightnessSplits[this.numberOfStops] || []
        if (steps.length === 0) {
            console.warn('No steps found for numberOfStops:', this.numberOfStops)
            return []
        }
        
        const { lRange, name } = this.getHueProfile(hueValue)
        
        const palette = []
        const baseIndex = steps.indexOf(500) // Find where base color should be (usually middle)
        
        /**
         * Chroma Modification (step 2)
         * NOTE: This includes the second chroma modification, with the first occuring in getMultiplierForHue.
         */
        steps.forEach((step, stepIndex) => {
            let l, c
            
            if (step === 500) {
                // Base color - use original values
                l = baseL
                c = baseC
            } else if (stepIndex < baseIndex) {
                // Lighter colors (50-400): interpolate from light end to base
                const progress = stepIndex / baseIndex // 0 to 1, where 1 = base (500)
                l = lRange[0] + (baseL - lRange[0]) * progress
                
                // Chroma: decrease as we go lighter
                const chromaProgress = stepIndex / baseIndex
                c = baseC * (0.2 + 0.8 * chromaProgress) // 20% to 100% of base chroma
            } else {
                // Darker colors (600-950): interpolate from base to dark end
                const darkerSteps = steps.length - 1 - baseIndex
                const darkIndex = stepIndex - baseIndex // 1-5 for 600-950
                const progress = darkIndex / darkerSteps // 0 to 1, where 0 = base (500)
                
                l = baseL + (lRange[1] - baseL) * progress
                
                // Chroma: increase as we go darker, with special boost for very dark colors
                if (step === 950) {
                    c = baseC * 1.6 // 60% boost for 950
                } else {
                    const chromaBoost = 1 + (progress * 0.3) // Up to 30% boost
                    c = baseC * chromaBoost
                }
            }
            
            // Create color with aggressive hue preservation
            let color = new Color('oklch', [l, c, hueValue])
            
            // Handle gamut mapping while preserving hue
            if (!color.inGamut('srgb')) {
                color = color.toGamut('srgb')
                
                // Force hue back to original after gamut mapping
                const mappedOklch = color.to('oklch').coords
                const preservedL = mappedOklch[0]
                const preservedC = mappedOklch[1]
                color = new Color('oklch', [preservedL, preservedC, hueValue])
                
                // If still out of gamut, reduce chroma while keeping hue
                let attempts = 0
                while (!color.inGamut('srgb') && attempts < 10) {
                    const currentOklch = color.to('oklch').coords
                    const reducedC = currentOklch[1] * 0.9
                    color = new Color('oklch', [currentOklch[0], reducedC, hueValue])
                    attempts++
                }
            }
            
            const finalHex = color.to('srgb').toString({ format: 'hex' })
            const finalOklch = new Color(finalHex).to('oklch').coords
            const finalCssColor = `oklch(${finalOklch[0]} ${finalOklch[1]} ${finalOklch[2]})`
            
            palette.push({
                index: stepIndex,
                step: step,
                lightness: finalOklch[0],
                hue: hueValue,
                chroma: finalOklch[1],
                oklch: {
                    l: finalOklch[0],
                    c: finalOklch[1],
                    h: finalOklch[2],
                    alpha: 1
                },
                hex: finalHex,
                cssColor: finalCssColor,
                profile: name,
                isBase: step === 500
            })
        })
        
        return palette
    },

    // Updated method to use advanced palette generation
    getLightnessColors(hueValue, baseChroma) {
        if (!this.baseColor) {
            console.warn('No base color available for lightness generation')
            return []
        }
        
        // Use the base color's lightness as the middle point
        const baseL = this.baseColor.l
        return this.generateAdvancedLightnessPalette(hueValue, baseL, baseChroma)
    },

    // Methods for Exporting
    updateColumnColors(columnIndex, columnData) {
        this.currentColumnColors[columnIndex] = columnData
    },

    getCurrentPaletteForExport() {
        return this.currentColumnColors.filter(columnData => columnData && columnData.colors && columnData.colors.length > 0)
    }
})